// @ts-nocheck
import {library} from "@fortawesome/fontawesome-svg-core";
import {fab} from "@fortawesome/free-brands-svg-icons";
import {far} from "@fortawesome/free-regular-svg-icons";
import {fas} from "@fortawesome/free-solid-svg-icons";
import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
import {ReactQueryDevtools} from "@tanstack/react-query-devtools";
import PropTypes from "prop-types";
import {lazy, Suspense, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {createBrowserRouter, Navigate, Outlet, RouterProvider, useLocation, useParams} from "react-router-dom";
import {ToastContainer} from "./components/BaseToast";
import DepositMethods from "./components/DepositMethods";
import BankOptions from "./components/DepositMethods/BankOptions";
import {ErrorBoundaryRedirect} from "./components/ErrorBoundaryHandler/ErrorBoundaryRedirect";
import FloatButtonWallet from "./components/FloatButton";
import FloatingMenu from "./components/FloatingMenu";
import AddPagoMovilForm from "./components/Forms/AddPagoMovil";
import ChangePassword from "./components/Forms/ChangePassword";
import RecoverPassword from "./components/Forms/RecoverPassword";
import RegistrationModal from "./components/Forms/Registration/RegistrationModal";
import LoginForm from "./components/Login";
import BaseModal from "./components/Modals";
import InstallationPrompt from "./components/Modals/InstallationPrompt";
import NotificationPrompt from "./components/Modals/NotificationPrompt";
import PagoMovilListModal from "./components/Modals/PagoMovilListModal";
import PagoMovilRegistrationModal from "./components/Modals/PagoMovilRegistrationModal";
import UpdatePrompt from "./components/Modals/UpdatePrompt";
import MultiGameFloatingButton from "./components/MultiGameFloatingButton";
import TransferSteps from "./components/TransferSteps";
import UserProfile from "./components/UserProfile";
import WithdrawalMethods from "./components/WithdrawalMethods";
import {DynamicTitleProvider} from "./hooks/useDynamicTitle";
import useGetAppConfig from "./hooks/useGetAppConfig";
import usePushNotifications from "./hooks/usePushNotifications";
import LOut from "./Layout";
import LoginRoute from "./pages/auth/Login/LoginRoute";
import RecoverPasswordRoute from "./pages/auth/RecoverPassword/RecoverPasswordRoute";
import RegistrationRoute from "./pages/auth/Registration/RegistrationRoute";
import MobileLogin from "./pages/MobileLogin";
import MobileRegistration from "./pages/MobileRegistration";
import ProtectedHomePage from "./pages/ProtectedHomePage";
import Bingo from "./pages/private/Bingo";
import ContactsRoute from "./pages/private/Contacts/ContactsRoute";
import DepositPagoMovilRoute from "./pages/private/DepositPagoMovil/DepositPagoMovilRoute";
import GameFeed from "./pages/private/GameFeed";
import GameLaunchRoute from "./pages/private/GameLaunch/GameLaunchRoute";
import GamesList from "./pages/private/Games";
import CrashGames from "./pages/private/CrashGames";
import CasinoList from "./pages/private/Games/components/CasinoList";
import GamesRoute from "./pages/private/GamesV2/GamesListRoute";
import Horses from "./pages/private/Horses";
import LiveCasino from "./pages/private/LiveCasino";
import Lotoven from "./pages/private/Lotoven";
import Lottery from "./pages/private/Lottery";
import DefaultLottery from "./pages/private/Lottery/Games/DefaultLottery";
import MyTickets from "./pages/private/Lottery/pages/MyTickets";
import LotteryV2Route from "./pages/private/LotteryV2/LotteryRoute";
import {ArticleDetailRoute, NewsListRoute} from "./pages/private/NewsV2";
import PagoMovilAccountsRoute from "./pages/private/PagoMovilAccounts/PagoMovilAccountsRoute";
import PaykuCallback from "./pages/private/PaykuCallback";
import ProfileRoute from "./pages/private/Profile/ProfileRoute";
import ProviderProfile from "./pages/private/ProviderProfile";
import RaffleTickets from "./pages/private/Raffles/pages/Tickets";
import RafflesRoute from "./pages/private/RafflesV2/RafflesRoute";
import ReferralsRoute from "./pages/private/Referrals/ReferralsRoute";
import Support from "./pages/private/Support";
import TransactionsRoute from "./pages/private/Transactions/TransactionsRoute";
import TransferRoute from "./pages/private/Transfer/TransferRoute";
import WalletRoute from "./pages/private/Wallet/WalletRoute";
import WithdrawalPagoMovilRoute from "./pages/private/WithdrawalPagoMovil/WithdrawalPagoMovilRoute";
import DeleteAccount from "./pages/public/DeleteAccount";
import HomeV2Route from "./pages/public/HomeV2/HomeRoute";
import TermsOfService from "./pages/public/TOS";
import AppContext from "./providers/AppContext";
import {initializeAmplitude} from "./providers/analytics";
import {useChangePassword} from "./store/useAccountScreens.store";
import {useAuthStore} from "./store/useAuth.store";
import {useBalanceStore} from "./store/useBalance.store";
import {useUserProfileInfo} from "./store/useUserProfileInfo.store";
import {useUserSupport} from "./store/useUserSupport.store";
import "./styles/button.css";

import {ConfEnv} from "./config/config";
import {initializeNativeBridge} from "./services/nativeBridge.service";
import {startSessionValidation, stopSessionValidation} from "./util/sessionManager";

const Parley = lazy(() => import("./pages/private/Parley"));
const Results = lazy(() => import("./pages/private/Lottery/Games/DefaultLottery/pages/Results"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      staleTime: 5 * (60 * 1000),
    },
  },
});

const ConfigLoader = () => {
  useGetAppConfig();
  return null;
};

const isMobile = window.innerWidth <= 600;

// Component to conditionally wrap routes with layout
const ScrollToTop = () => {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo({top: 0, left: 0, behavior: "instant"});
  }, [location.pathname]);
  return null;
};

const ConditionalLayout = ({children}) => {
  const location = useLocation();
  const noLayoutPaths = [
    "/mobile-login",
    "/mobile-registration",
    "/payku/callback",
    "/auth/login",
    "/auth/register",
    "/auth/recover",
    "/home/v2",
    "/wallet",
    "/games",
    "/user/transactions",
    "/wallet/deposit/pago-movil",
    "/wallet/withdrawal/pago-movil",
    "/wallet/transfer",
    "/user/pago-movil",
    "/user/contacts",
    "/user/profile",
    "/user/referrals",
    "/lottery/v2",
    "/raffles",
    "/news",
  ];
  const isGameLaunch =
    /^\/games\/[^/]+\/[^/]+$/.test(location.pathname) &&
    !location.pathname.startsWith("/games/category/") &&
    !location.pathname.startsWith("/games/provider/");
  const isArticle = /^\/article\/[^/]+$/.test(location.pathname);
  const shouldShowLayout = !noLayoutPaths.includes(location.pathname) && !isGameLaunch && !isArticle;
  const hideFloatingButtons = location.pathname.startsWith("/provider/") || location.pathname.startsWith("/game-feed");

  if (!shouldShowLayout) {
    return <><ScrollToTop />{children}</>;
  }

  return (
    <LOut>
      <ScrollToTop />
      {!hideFloatingButtons && <FloatButtonWallet />}
      <FloatingMenu
        onMenuToggle={() => {}}
        shouldCollapse={false}
      />
      <MultiGameFloatingButton />
      {children}
    </LOut>
  );
};

ConditionalLayout.propTypes = {
  children: PropTypes.node.isRequired,
};

// Wrapper to extract gameIdString from params and pass to DefaultLottery
const LotteryGameRoute = () => {
  const {gameIdString} = useParams();
  return <DefaultLottery gameIdString={gameIdString} />;
};

const RootLayout = () => {
  const {t} = useTranslation();
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);

  const [depositModalType, setDepositModalType] = useState("depositMethods");
  const [depositModalWidth, setDepositModalWidth] = useState("678px");
  const [withdrawalModalWidth, setWithdrawalModalWidth] = useState("678px");

  const validateSession = useAuthStore(state => state.validateSession);
  const authStatus = useAuthStore(state => state.authStatus);
  const visible = useAuthStore(state => state.showAuthModal);
  const authForm = useAuthStore(state => state.authForm);
  const handleShowAuthModal = useAuthStore(state => state.handleShowAuthModal);
  const bankAccountModalVisible = useBalanceStore(state => state.bankAccountModalVisible);
  const handleBankAccountModal = useBalanceStore(state => state.handleBankAccountModal);
  const withdrawalModalVisible = useBalanceStore(state => state.withdrawalModalVisible);
  const handleWithdrawalModal = useBalanceStore(state => state.handleWithdrawalModal);

  const transferModalVisible = useBalanceStore(state => state.transferModalVisible);
  const handleTransferModal = useBalanceStore(state => state.handleTransferModal);

  const addPagoMovilModalVisible = useBalanceStore(state => state.addPagoMovilModalVisible);
  const handleAddPagoMovilModal = useBalanceStore(state => state.handleAddPagoMovilModal);

  const handleChangePasswordModal = useChangePassword(state => state.handleChangePasswordModal);
  const changePasswordModalVisible = useChangePassword(state => state.changePasswordModalVisible);
  const userProfileModaVisible = useUserProfileInfo(state => state.userProfileModaVisible);
  const handleProfileModal = useUserProfileInfo(state => state.handleProfileModal);

  const userSupportModaVisible = useUserSupport(state => state.userSupportModaVisible);
  const handleSupportModal = useUserSupport(state => state.handleSupportModal);

  const userId = useAuthStore(state => state.user?.email);

  const {
    pushSupported,
    userConsent,
    onRequestPermission,
    isSubscribing,
    error: pushError,
  } = usePushNotifications(userId);

  useEffect(() => {
    if (ConfEnv.ENV !== "development" && authStatus === "authenticated" && pushSupported && userConsent === "default") {
      const timer = setTimeout(() => {
        setShowNotificationPrompt(true);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [authStatus, pushSupported, userConsent]);

  useEffect(() => {
    startSessionValidation(validateSession);

    return () => {
      stopSessionValidation();
    };
  }, []);

  useEffect(() => {
    initializeAmplitude(userId);
  }, [userId]);

  useEffect(() => {
    initializeNativeBridge();
  }, []);

  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [installPromptEvent, setInstallPromptEvent] = useState(null);
  const [installDeviceType, setInstallDeviceType] = useState(null);

  useEffect(() => {
    const handleBeforeInstallPrompt = e => {
      e.preventDefault();
      setInstallPromptEvent(e);

      const userAgent = navigator.userAgent.toLowerCase();
      if (userAgent.includes("android")) {
        setInstallDeviceType("android");
      } else if (userAgent.includes("iphone") || userAgent.includes("ipad") || userAgent.includes("ipod")) {
        setInstallDeviceType("ios");
      } else {
        setInstallDeviceType("desktop");
      }

      if (window.location.pathname !== "/payku/callback") {
        setShowInstallPrompt(true);
      }
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    };
  }, []);

  return (
    <>
      <ToastContainer />
      <BaseModal
        title={t("baseModalTitleSupport")}
        state={userSupportModaVisible}
        closeModal={handleSupportModal}
        width="400px"
      >
        <Support />
      </BaseModal>

      <Suspense fallback={<div>Loading...</div>}>
        <ConditionalLayout>
          <Outlet />
        </ConditionalLayout>
      </Suspense>

      <BaseModal
        state={visible}
        closeModal={handleShowAuthModal}
        width={
          authForm === "registration"
            ? window.innerWidth <= 768
              ? "92vw"
              : "920px"
            : authForm === "recover"
              ? "530px"
              : window.innerWidth <= 768
                ? "80vw"
                : "950px"
        }
        className={
          authForm === "login"
            ? "auth-modal !bg-transparent !border-0 !shadow-none !rounded-none !max-w-none !w-auto [&>div:last-child]:!p-0"
            : authForm === "registration"
              ? "register-modal !bg-transparent !border-0 !shadow-none !rounded-none !max-w-none !w-auto [&>div:last-child]:!p-0"
              : ""
        }
        wrapClassName={authForm === "login" ? "auth-modal" : ""}
      >
        {authForm === "login" && <LoginForm onClose={handleShowAuthModal} />}
        {authForm === "recover" && <RecoverPassword />}
        {authForm === "registration" && <RegistrationModal onClose={handleShowAuthModal} />}
      </BaseModal>

      <BaseModal
        title=""
        state={userSupportModaVisible}
        closeModal={handleSupportModal}
        width="1024px"
      >
        <Support />
      </BaseModal>

      <BaseModal
        title=""
        state={userProfileModaVisible}
        closeModal={handleProfileModal}
        width="500px"
        className="profile-modal"
      >
        <UserProfile />
      </BaseModal>

      <BaseModal
        title={<h4 style={{textAlign: "center", fontWeight: "bold"}}>{t("baseModalTitleMobilePayment")}</h4>}
        state={bankAccountModalVisible}
        closeModal={handleBankAccountModal}
        width={depositModalWidth}
      >
        {depositModalType === "bankOptions" ? (
          <BankOptions
            prev={() => setDepositModalType("depositMethods")}
            onWidthChange={setDepositModalWidth}
          />
        ) : (
          <DepositMethods
            next={() => setDepositModalType("bankOptions")}
            onWidthChange={setDepositModalWidth}
          />
        )}
      </BaseModal>

      <BaseModal
        title={
          <h4
            style={{
              fontWeight: 500,
              paddingLeft: isMobile ? 0 : 50,
            }}
          >
            {t("baseModalTitleChangePassword")}
          </h4>
        }
        state={changePasswordModalVisible}
        closeModal={handleChangePasswordModal}
        width="678px"
      >
        <ChangePassword />
      </BaseModal>

      <BaseModal
        title={
          <h4
            style={{
              textAlign: "center",
              fontSize: isMobile ? "18px" : "25px",
              fontWeight: "700",
            }}
          >
            {t("baseModalTitleWithdrawal")}
          </h4>
        }
        state={withdrawalModalVisible}
        closeModal={handleWithdrawalModal}
        width={withdrawalModalWidth}
      >
        <WithdrawalMethods
          setWithdrawalModalWidth={setWithdrawalModalWidth}
          closeModal={handleWithdrawalModal}
        />
      </BaseModal>

      <BaseModal
        title={
          <h4
            style={{
              textAlign: "center",
              fontSize: isMobile ? "18px" : "25px",
              fontWeight: "700",
            }}
          >
            {t("baseModalTitleTransfer")}
          </h4>
        }
        state={transferModalVisible}
        closeModal={handleTransferModal}
        width="930px"
      >
        <TransferSteps />
      </BaseModal>

      <BaseModal
        title={t("mobilePaymentAdd")}
        state={addPagoMovilModalVisible}
        closeModal={handleAddPagoMovilModal}
      >
        <AddPagoMovilForm onSuccess={handleAddPagoMovilModal} />
      </BaseModal>
      <BaseModal
        state={showNotificationPrompt}
        closeModal={() => setShowNotificationPrompt(false)}
        className="notification-prompt-modal"
        width="607px"
        closable={false}
      >
        <NotificationPrompt
          onClose={() => setShowNotificationPrompt(false)}
          onRequestPermission={() => {
            onRequestPermission();
            setShowNotificationPrompt(false);
          }}
          pushSupported={pushSupported}
          userConsent={userConsent}
          isSubscribing={isSubscribing}
          error={pushError}
        />
      </BaseModal>
      <BaseModal
        state={showInstallPrompt}
        closeModal={() => setShowInstallPrompt(false)}
      >
        <InstallationPrompt
          deviceType={installDeviceType}
          promptEvent={installPromptEvent}
          onClose={() => setShowInstallPrompt(false)}
        />
      </BaseModal>

      {authStatus === "authenticated" && (
        <>
          <PagoMovilRegistrationModal />
          <PagoMovilListModal />
        </>
      )}

      <UpdatePrompt />
    </>
  );
};

const router = createBrowserRouter(
  [
    {
      element: <RootLayout />,
      children: [
        {path: "/", element: <ProtectedHomePage />},
        {path: "/mobile-login", element: <MobileLogin />},
        {path: "/mobile-registration", element: <MobileRegistration />},
        {path: "/auth/login", element: <LoginRoute />},
        {path: "/auth/register", element: <RegistrationRoute />},
        {path: "/auth/recover", element: <RecoverPasswordRoute />},
        {path: "/home/v2", element: <HomeV2Route />},
        {path: "/wallet", element: <WalletRoute />},
        {path: "/games", element: <GamesRoute />},
        {path: "/wallet/deposit/pago-movil", element: <DepositPagoMovilRoute />},
        {path: "/wallet/withdrawal/pago-movil", element: <WithdrawalPagoMovilRoute />},
        {path: "/wallet/transfer", element: <TransferRoute />},
        {path: "/user/profile", element: <ProfileRoute />},
        {path: "/user/referrals", element: <ReferralsRoute />},
        {path: "/lottery/v2", element: <LotteryV2Route />},
        {path: "/terminos-y-condiciones", element: <TermsOfService />},
        {path: "/eliminar-cuenta", element: <DeleteAccount />},
        {path: "/payku/callback", element: <PaykuCallback />},
        {
          path: "/games/category/:category",
          element: (
            <ErrorBoundaryRedirect>
              <GamesList />
            </ErrorBoundaryRedirect>
          ),
        },
        {
          path: "/games/provider/:provider",
          element: (
            <ErrorBoundaryRedirect>
              <GamesList />
            </ErrorBoundaryRedirect>
          ),
        },
        {
          path: "/gameslist/all",
          element: (
            <ErrorBoundaryRedirect>
              <GamesList />
            </ErrorBoundaryRedirect>
          ),
        },
        {
          path: "/gameslist/board-games",
          element: <Navigate to="/games/category/board-games" replace />,
        },
        {
          path: "/crash-games",
          element: (
            <ErrorBoundaryRedirect>
              <CrashGames />
            </ErrorBoundaryRedirect>
          ),
        },
        {
          path: "/games/:provider/:gameId",
          element: (
            <ErrorBoundaryRedirect>
              <GameLaunchRoute />
            </ErrorBoundaryRedirect>
          ),
        },
        {
          path: "/lottery",
          element: (
            <ErrorBoundaryRedirect>
              <Lottery />
            </ErrorBoundaryRedirect>
          ),
        },
        {
          path: "/lottery/:category",
          element: (
            <ErrorBoundaryRedirect>
              <Lottery />
            </ErrorBoundaryRedirect>
          ),
        },
        {
          path: "/lottery/:gameIdString",
          element: (
            <ErrorBoundaryRedirect>
              <LotteryGameRoute />
            </ErrorBoundaryRedirect>
          ),
        },
        {path: "/lottery/:gameId/results", element: <Results />},
        {path: "/lottery/tickets", element: <MyTickets />},
        {path: "/raffles", element: <RafflesRoute />},
        {path: "/raffles/tickets", element: <RaffleTickets />},
        {path: "/live-casino", element: <LiveCasino />},
        {
          path: "/bingo",
          element: (
            <ErrorBoundaryRedirect>
              <Bingo />
            </ErrorBoundaryRedirect>
          ),
        },
        {
          path: "/horses",
          element: (
            <ErrorBoundaryRedirect>
              <Horses />
            </ErrorBoundaryRedirect>
          ),
        },
        {path: "/news", element: <NewsListRoute />},
        {path: "/article/:slug", element: <ArticleDetailRoute />},
        {
          path: "/game-feed",
          element: (
            <ErrorBoundaryRedirect>
              <GameFeed />
            </ErrorBoundaryRedirect>
          ),
        },
        {
          path: "/provider/:provider",
          element: (
            <ErrorBoundaryRedirect>
              <ProviderProfile />
            </ErrorBoundaryRedirect>
          ),
        },
        {
          path: "/casino",
          element: (
            <ErrorBoundaryRedirect>
              <CasinoList />
            </ErrorBoundaryRedirect>
          ),
        },
        {
          path: "/sports",
          element: (
            <ErrorBoundaryRedirect>
              <Parley />
            </ErrorBoundaryRedirect>
          ),
        },
        {path: "/user/contacts", element: <ContactsRoute />},
        {path: "/user/pago-movil", element: <PagoMovilAccountsRoute />},
        {path: "/user/transactions", element: <TransactionsRoute />},
        {path: "/lotoven", element: <Lotoven />},
        {path: "/lotoven/home", element: <Lotoven />},
        {path: "/lotoven/animalitos", element: <Lotoven />},
        {path: "/lotoven/loterias", element: <Lotoven />},
      ],
    },
  ],
  {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    },
  }
);

const App = () => {
  const iframeRef = useRef(null);
  return (
    <AppContext.Provider value={iframeRef}>
      <QueryClientProvider client={queryClient}>
        <DynamicTitleProvider>
          <ConfigLoader />
          <RouterProvider router={router} />
          <ReactQueryDevtools />
        </DynamicTitleProvider>
      </QueryClientProvider>
    </AppContext.Provider>
  );
};

export default App;
library.add(fab, fas, far);
